/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef, MutableRefObject } from 'react';
import { Theme, Typography, useTheme, Skeleton, Box } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { detectAgent } from 'core/utils';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/_common/models/redux/store';
import { toggleKeyboardAction } from '@Common/actions/commonActions';
import ReactDOMServer from 'react-dom/server';
import { getOutputKey, filterOutputSummary } from '../handlers/logic';
import { OutputAbs } from '../models/store';
import { AbsUnionGuard } from '../guards/union';
import { textBoxesMinHeight } from '../mConstants';
import { keywordHighlight } from '../handlers/contextHighlight';

const useStyles = makeStyles((theme) =>
  createStyles({
    skeleton: {
      backgroundColor: theme.palette.grey[500], // check
    },
    ndInputText: {
      width: '100%',
      padding: '20px 25px 20px 20px',
      'text-rendering': 'optimizeLegibility',
      '-webkit-font-smoothing': 'antialiased',
      fontSize: '16px',
      fontWeight: 400,
      outline: 'none',
      lineHeight: '1.5em',
      border: 'none',
      display: 'inline-block',
      minHeight: ' calc(50vh - 0px)',
      [theme.breakpoints.down(768)]: {
        padding: '20px 13px 20px 13px',
        minHeight: ' calc(100vh - 260px)',
      },
    },

    smzrOuputBox: {
      '& li': {
        marginBottom: '20px',
      },
    },
  })
);

function OutputBox() {
  const classes = useStyles();
  const [isContentEditable, setIsContentEditable] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const inputText = useSelector((state: RootState) => state.smzr.input.inputPara);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension);
  const extSentenceSeq = useSelector(
    (state: RootState) => state.smzr.output.ext.summarySentenceSequence
  );
  const extSentences = useSelector((state: RootState) => state.smzr.output.ext.sentences);

  let absSummary = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });
  const summarizedOutput = useRef() as MutableRefObject<HTMLInputElement>;
  const selectedKeywords = useSelector((state: RootState) => state.smzr.controls.selectedKeywords);
  const outputKeywords = useSelector((state: RootState) => state.smzr.output.outputKeywords);
  const theme = useTheme<Theme>();
  const dispatch = useDispatch();

  useEffect(() => {
    setIsMobile(!!detectAgent().isMobile);
  });

  // ContentEditable set to false on skeleton and true while summary is shown.
  useEffect(() => {
    if ((!extSentenceSeq || !extSentenceSeq.length) && !absSummary) {
      setIsContentEditable(false);
      return;
    }
    if (mode === 'ext' || mode === 'abs') {
      setIsContentEditable(true);
    }
    if (mode === 'abs' && selectedKeywords?.length) {
      summarizedOutput.current.innerHTML = keywordHighlight(
        summarizedOutput.current.innerText,
        [...selectedKeywords, ...(outputKeywords as string[])],
        theme
      );
    }
  }, [extSentenceSeq, absSummary]);

  const GetOutputContent = () => {
    if ((!extSentenceSeq || !extSentenceSeq.length) && !absSummary) {
      if (isMobile) {
        return (
          <Box fontSize="16px" pt="4px" color="#828282">
            Your summarized text will appear here after summarization.
          </Box>
        );
      }
      return (
        <div>
          <Skeleton variant="text" classes={{ root: classes.skeleton }} animation={false} />
          <Skeleton
            variant="text"
            classes={{ root: classes.skeleton }}
            width="75%"
            animation={false}
          />
          <Skeleton
            variant="text"
            classes={{ root: classes.skeleton }}
            width="50%"
            animation={false}
          />
        </div>
      );
    }
    if (mode === 'ext') {
      return (
        <>
          {extSentenceSeq.map((sentenceId: keyof typeof extSentences) => (
            <li key={sentenceId}>{extSentences[sentenceId].text}</li>
          ))}
        </>
      );
    }
    if (mode === 'abs') {
      absSummary = filterOutputSummary(absSummary);

      // Regex check logic
      const optionalRegex = /(^|\s*)(CNN\.)/gim;
      const requiredRegex =
        /(\s*)(back\s*to\s*the\s*(page(s?)|site)\s*you\s*(came|were)\s*(from|to):?|Back to Mail Online home)(\s*\.)/gim;
      // const match = absSummary.match(requiredRegex);
      // console.log(match);
      absSummary = absSummary.replace(requiredRegex, '');
      if (!optionalRegex.test(inputText)) {
        absSummary = absSummary.replace(optionalRegex, '');
      }
      // const summParas = absSummary.split('\n').map((item, i) => <p key={i}>{item}</p>);
      // console.log(summParas);
      return <>{absSummary}</>;
    }
    return null;
  };

  const htmlString = ReactDOMServer.renderToString(<GetOutputContent />);

  return (
    <div className={classes.ndInputText}>
      <div
        ref={summarizedOutput}
        id="outputBoxSummarizer"
        data-gramm_editor="false"
        contentEditable={isContentEditable}
        wt-ignore-input="true"
        suppressContentEditableWarning
        className={classes.smzrOuputBox}
        data-placeholder={
          isMobile ? 'Your summarized text will appear here after summarization.' : ''
        }
        style={{
          minHeight: `${textBoxesMinHeight()}`,
          fontSize: isExtension ? '14px' : '16px',
          paddingRight: '30px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowY: 'auto',
          display: 'inline-block',
          width: '100%',
        }}
        onFocus={() => {
          if (isMobile) {
            dispatch(toggleKeyboardAction(true));
            setTimeout(() => {
              window.scrollBy({
                top: -65,
                behavior: 'smooth',
              });
            }, 0);
          }
        }}
        onBlur={(e) => {
          // console.log('[Summarizer Output on Blur]');
          if (isMobile && e.target.id !== 'outputBoxSummarizer') {
            dispatch(toggleKeyboardAction(false));
            // setTimeout(() => {}, 100);
          }
        }}
        dangerouslySetInnerHTML={{ __html: htmlString }}
      />
    </div>
  );
}

export default OutputBox;
